import React, { useState, useEffect } from 'react'
import {
  Users,
  Search,
  DollarSign,
  Eye,
  Plus,
  RefreshCw,
  CreditCard,
  Building,
  CheckCircle2,
  AlertCircle,
  FileText,
  X,
  ArrowUpRight,
  ArrowDownLeft,
  UserPlus,
  Download,
  FileSpreadsheet
} from 'lucide-react'
import toast from 'react-hot-toast'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import * as XLSX from 'xlsx'
import customerService from '../../../services/customerService'
import branchService from '../../../services/branchService'
import SearchableSelect from '../../../components/common/SearchableSelect'

export function CreditCustomers() {
  const [customers, setCustomers] = useState([])
  const [allExistingCustomers, setAllExistingCustomers] = useState([])
  const [branches, setBranches] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedBranch, setSelectedBranch] = useState('')

  // Stats
  const [totalOutstanding, setTotalOutstanding] = useState(0)

  // Modals state
  const [payModalOpen, setPayModalOpen] = useState(false)
  const [selectedCustomerForPay, setSelectedCustomerForPay] = useState(null)
  const [payAmount, setPayAmount] = useState('')
  const [isFullPayment, setIsFullPayment] = useState(true)
  const [paymentMethod, setPaymentMethod] = useState('cash')
  const [referenceNumber, setReferenceNumber] = useState('')
  const [notes, setNotes] = useState('')
  const [submittingPay, setSubmittingPay] = useState(false)

  // History modal
  const [historyModalOpen, setHistoryModalOpen] = useState(false)
  const [historyCustomer, setHistoryCustomer] = useState(null)
  const [historyTransactions, setHistoryTransactions] = useState([])
  const [loadingHistory, setLoadingHistory] = useState(false)

  // Add Credit Customer modal
  const [addModalOpen, setAddModalOpen] = useState(false)
  const [selectedAddCustomerId, setSelectedAddCustomerId] = useState('')
  const [addCreditLimit, setAddCreditLimit] = useState('0')
  const [submittingAdd, setSubmittingAdd] = useState(false)

  useEffect(() => {
    fetchCreditCustomers()
    fetchBranches()
  }, [selectedBranch])

  const fetchCreditCustomers = async () => {
    setLoading(true)
    try {
      const params = {}
      if (selectedBranch && selectedBranch.trim() !== '') params.branch = selectedBranch
      if (searchTerm && searchTerm.trim() !== '') params.search = searchTerm

      const res = await customerService.getCreditCustomers(params)
      const data = res.data?.data || res.data || {}
      setCustomers(data.customers || [])
      setTotalOutstanding(data.totalOutstandingCredit || 0)
    } catch (err) {
      console.error('Error fetching credit customers:', err)
      toast.error('Failed to load credit customers')
    } finally {
      setLoading(false)
    }
  }

  const fetchBranches = async () => {
    try {
      const res = await branchService.getAllBranches()
      setBranches(res.data?.data || res.data || [])
    } catch (err) {
      console.error('Error fetching branches:', err)
    }
  }

  const fetchAllNonCreditCustomers = async () => {
    try {
      const res = await customerService.getAllCustomers({ limit: 100 })
      const list = res.data?.data?.customers || res.data?.customers || res.customers || []
      // Filter out customers already marked as credit customer
      const available = list.filter(c => !c.isCreditCustomer)
      setAllExistingCustomers(available)
    } catch (err) {
      console.error('Error fetching non-credit customers:', err)
      toast.error('Failed to load customer list')
    }
  }

  const openPayModal = (cust) => {
    setSelectedCustomerForPay(cust)
    const due = cust.creditBalance || 0
    setPayAmount(due.toString())
    setIsFullPayment(true)
    setPaymentMethod('cash')
    setReferenceNumber('')
    setNotes('')
    setPayModalOpen(true)
  }

  const handlePayAmountChange = (val) => {
    setPayAmount(val)
    const num = parseFloat(val) || 0
    const due = selectedCustomerForPay?.creditBalance || 0
    if (Math.abs(num - due) < 0.01) {
      setIsFullPayment(true)
    } else {
      setIsFullPayment(false)
    }
  }

  const handleToggleFullPayment = () => {
    const due = selectedCustomerForPay?.creditBalance || 0
    if (!isFullPayment) {
      setPayAmount(due.toString())
      setIsFullPayment(true)
    } else {
      setIsFullPayment(false)
    }
  }

  const handleSubmitPayment = async (e) => {
    e.preventDefault()
    if (!selectedCustomerForPay) return

    const amount = parseFloat(payAmount)
    const due = selectedCustomerForPay.creditBalance || 0

    if (!amount || amount <= 0) {
      toast.error('Please enter a valid payment amount greater than 0')
      return
    }

    if (amount > due + 0.01) {
      toast.error(`Payment amount cannot exceed total balance due (Rs. ${due.toLocaleString()})`)
      return
    }

    setSubmittingPay(true)
    try {
      await customerService.recordCreditPayment(selectedCustomerForPay._id, {
        amountPaid: amount,
        paymentMethod,
        referenceNumber,
        notes
      })

      toast.success(`Payment of Rs. ${amount.toLocaleString()} recorded successfully!`)
      setPayModalOpen(false)
      fetchCreditCustomers()
    } catch (err) {
      console.error('Payment error:', err)
      toast.error(err.response?.data?.message || 'Failed to record credit payment')
    } finally {
      setSubmittingPay(false)
    }
  }

  const openHistoryModal = async (cust) => {
    setHistoryCustomer(cust)
    setHistoryModalOpen(true)
    setLoadingHistory(true)
    try {
      const res = await customerService.getCustomerCreditHistory(cust._id)
      const data = res.data?.data || res.data || {}
      setHistoryTransactions(data.transactions || [])
    } catch (err) {
      console.error('Error fetching history:', err)
      toast.error('Failed to load credit history')
    } finally {
      setLoadingHistory(false)
    }
  }

  const downloadCreditHistoryPDF = async (customer, transactions = []) => {
    try {
      if (!customer) return;
      toast.loading('Generating PDF Statement...', { id: 'pdf-statement' });

      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();

      // Brand Header
      doc.setFillColor(249, 115, 22); // Orange header
      doc.rect(0, 0, pageWidth, 24, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(15);
      doc.setTextColor(255, 255, 255);
      doc.text('TEZLAA CAFE - CREDIT ACCOUNT STATEMENT', 14, 15);

      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth - 14, 15, { align: 'right' });

      // Customer Details Box
      doc.setDrawColor(226, 232, 240);
      doc.setFillColor(248, 250, 252);
      doc.roundedRect(14, 30, pageWidth - 28, 26, 3, 3, 'FD');

      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 23, 42);
      doc.text(`Customer: ${customer.name || 'N/A'}`, 20, 39);

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(71, 85, 105);
      doc.text(`Mobile: ${customer.mobileNumber || 'N/A'}`, 20, 49);
      doc.text(`Branch: ${customer.branch?.name || 'All Branches'}`, 90, 49);

      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      const due = customer.creditBalance || 0;
      doc.setTextColor(due > 0 ? 220 : 22, due > 0 ? 38 : 163, due > 0 ? 38 : 74);
      doc.text(`Current Due: Rs. ${due.toFixed(2)}`, pageWidth - 20, 44, { align: 'right' });

      // Build Table Data
      const tableRows = transactions.map((tx) => [
        new Date(tx.createdAt).toLocaleString(),
        tx.type === 'CHARGE' ? 'CHARGE (POS Sale)' : 'SETTLEMENT (Payment)',
        tx.order ? (tx.order.orderNumber || tx.order._id) : (tx.referenceNumber || '-'),
        tx.paymentMethod ? tx.paymentMethod.toUpperCase() : '-',
        `${tx.type === 'CHARGE' ? '+' : '-'} Rs. ${(tx.amount || 0).toFixed(2)}`,
        `Rs. ${(tx.previousBalance || 0).toFixed(2)}`,
        `Rs. ${(tx.newBalance || 0).toFixed(2)}`,
        tx.notes || tx.recordedBy?.username || '-'
      ]);

      const tableConfig = {
        startY: 62,
        head: [['Date & Time', 'Type', 'Order / Ref #', 'Method', 'Amount', 'Prev Bal', 'New Bal', 'Notes / User']],
        body: tableRows,
        theme: 'striped',
        headStyles: {
          fillColor: [30, 41, 59],
          textColor: [255, 255, 255],
          fontSize: 8,
          fontStyle: 'bold'
        },
        bodyStyles: {
          fontSize: 8,
          textColor: [51, 65, 85]
        },
        columnStyles: {
          0: { cellWidth: 34 },
          1: { cellWidth: 26 },
          2: { cellWidth: 30 },
          3: { cellWidth: 18 },
          4: { cellWidth: 22, halign: 'right', fontStyle: 'bold' },
          5: { cellWidth: 20, halign: 'right' },
          6: { cellWidth: 20, halign: 'right', fontStyle: 'bold' },
          7: { cellWidth: 'auto' }
        },
        margin: { left: 14, right: 14 }
      };

      if (typeof autoTable === 'function') {
        autoTable(doc, tableConfig);
      } else if (typeof doc.autoTable === 'function') {
        doc.autoTable(tableConfig);
      } else {
        const autoTableModule = await import('jspdf-autotable');
        const runner = autoTableModule.default || autoTableModule;
        runner(doc, tableConfig);
      }

      const fileName = `Credit_Statement_${(customer.name || 'Customer').replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);

      toast.success('PDF Statement downloaded successfully!', { id: 'pdf-statement' });
    } catch (err) {
      console.error('Error generating PDF statement:', err);
      toast.error('Failed to generate PDF statement', { id: 'pdf-statement' });
    }
  };

  const downloadCreditHistoryExcel = (customer, transactions = []) => {
    try {
      if (!customer) return;
      toast.loading('Generating Excel Statement...', { id: 'excel-statement' });

      const ledgerRows = transactions.map((tx) => ({
        'Date & Time': new Date(tx.createdAt).toLocaleString(),
        'Transaction Type': tx.type === 'CHARGE' ? 'CHARGE (POS Sale)' : 'SETTLEMENT (Payment)',
        'Order Number / Reference': tx.order ? (tx.order.orderNumber || tx.order._id) : (tx.referenceNumber || '-'),
        'Payment Method': tx.paymentMethod ? tx.paymentMethod.toUpperCase() : '-',
        'Amount (Rs.)': tx.type === 'CHARGE' ? tx.amount : -tx.amount,
        'Previous Balance (Rs.)': tx.previousBalance || 0,
        'New Balance (Rs.)': tx.newBalance || 0,
        'Recorded By': tx.recordedBy?.username || 'System',
        'Notes': tx.notes || ''
      }));

      const worksheet = XLSX.utils.json_to_sheet(ledgerRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Credit Ledger');

      const fileName = `Credit_Ledger_${(customer.name || 'Customer').replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      toast.success('Excel Statement downloaded successfully!', { id: 'excel-statement' });
    } catch (err) {
      console.error('Error exporting credit history to Excel:', err);
      toast.error('Failed to export Excel statement', { id: 'excel-statement' });
    }
  };

  const handleDownloadRowStatement = async (cust, format = 'pdf') => {
    try {
      toast.loading(`Fetching statement for ${cust.name}...`, { id: 'fetch-statement' });
      const res = await customerService.getCustomerCreditHistory(cust._id);
      const data = res.data?.data || res.data || {};
      const txs = data.transactions || [];
      toast.dismiss('fetch-statement');

      if (txs.length === 0) {
        toast.error('No credit transactions recorded yet for this customer.');
        return;
      }

      if (format === 'pdf') {
        downloadCreditHistoryPDF(cust, txs);
      } else {
        downloadCreditHistoryExcel(cust, txs);
      }
    } catch (err) {
      console.error('Error fetching history for statement download:', err);
      toast.error('Failed to download credit statement', { id: 'fetch-statement' });
    }
  };

  const openAddModal = () => {
    setSelectedAddCustomerId('')
    setAddCreditLimit('0')
    fetchAllNonCreditCustomers()
    setAddModalOpen(true)
  }

  const handleAddCreditCustomer = async (e) => {
    e.preventDefault()
    if (!selectedAddCustomerId) {
      toast.error('Please select a customer')
      return
    }

    setSubmittingAdd(true)
    try {
      await customerService.toggleCreditCustomerStatus(selectedAddCustomerId, true)
      toast.success('Customer added to Credit Customers successfully!')
      setAddModalOpen(false)
      fetchCreditCustomers()
    } catch (err) {
      console.error('Add credit customer error:', err)
      toast.error(err.response?.data?.message || 'Failed to add credit customer')
    } finally {
      setSubmittingAdd(false)
    }
  }

  const filteredCustomers = customers.filter((cust) => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      const nameMatch = cust.name?.toLowerCase().includes(term)
      const phoneMatch = cust.mobileNumber?.includes(term)
      if (!nameMatch && !phoneMatch) return false
    }
    return true
  })

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Credit Customers</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage customer credit limits, outstanding balances, settlements, and history.
          </p>
        </div>
        <button
          onClick={openAddModal}
          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-xl shadow-sm transition-all"
        >
          <UserPlus className="w-4 h-4" />
          <span>Add Credit Customer</span>
        </button>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500">Total Credit Customers</p>
            <h3 className="text-3xl font-bold text-gray-900 mt-2">{customers.length}</h3>
          </div>
          <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center text-orange-500">
            <Users className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500">Total Outstanding Balance Due</p>
            <h3 className="text-3xl font-bold text-red-600 mt-2">
              Rs. {totalOutstanding.toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </h3>
          </div>
          <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center text-red-500">
            <DollarSign className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500">Active Accounts</p>
            <h3 className="text-3xl font-bold text-green-600 mt-2">
              {customers.filter(c => c.isActive).length}
            </h3>
          </div>
          <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center text-green-500">
            <CheckCircle2 className="w-6 h-6" />
          </div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="relative w-full md:w-96">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by customer name or phone number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
          />
        </div>

        {branches.length > 0 && (
          <div className="flex items-center gap-2 w-full md:w-auto">
            <Building className="w-4 h-4 text-gray-400" />
            <select
              value={selectedBranch}
              onChange={(e) => setSelectedBranch(e.target.value)}
              className="w-full md:w-60 p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
            >
              <option value="">All Branches</option>
              {branches.map((b) => (
                <option key={b._id} value={b._id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Customers Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-500 flex flex-col items-center gap-2">
            <RefreshCw className="w-8 h-8 animate-spin text-orange-500" />
            <span>Loading credit customers...</span>
          </div>
        ) : filteredCustomers.length === 0 ? (
          <div className="p-12 text-center text-gray-500 space-y-3">
            <Users className="w-12 h-12 text-gray-300 mx-auto" />
            <h4 className="text-lg font-semibold text-gray-700">No Credit Customers Found</h4>
            <p className="text-sm text-gray-500">
              Click "+ Add Credit Customer" to tag an existing customer as a credit customer.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-gray-600 font-semibold uppercase text-xs">
                  <th className="py-3 px-4">Customer</th>
                  <th className="py-3 px-4">Mobile Number</th>
                  <th className="py-3 px-4">Branch</th>
                  <th className="py-3 px-4 text-right">Credit Balance Due</th>
                  <th className="py-3 px-4 text-center">Status</th>
                  <th className="py-3 px-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredCustomers.map((cust) => {
                  const due = cust.creditBalance || 0
                  return (
                    <tr key={cust._id} className="hover:bg-orange-50/40 transition-colors">
                      <td className="py-3 px-4 font-medium text-gray-900">
                        {cust.name}
                      </td>
                      <td className="py-3 px-4 text-gray-600 font-mono">
                        {cust.mobileNumber}
                      </td>
                      <td className="py-3 px-4 text-gray-600">
                        {cust.branch?.name || '-'}
                      </td>
                      <td className="py-3 px-4 text-right font-bold text-gray-900 font-mono text-base">
                        <span className={due > 0 ? 'text-red-600' : 'text-green-600'}>
                          Rs. {due.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        {due > 0 ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                            <AlertCircle className="w-3.5 h-3.5" />
                            Balance Due
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            Clear
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          {/* Dollar sign Pay Action Button */}
                          <button
                            onClick={() => openPayModal(cust)}
                            title="Pay Credit Balance"
                            disabled={due <= 0}
                            className={`p-2 rounded-lg transition-colors flex items-center gap-1 text-xs font-semibold ${
                              due > 0
                                ? 'bg-green-500 hover:bg-green-600 text-white shadow-sm'
                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            }`}
                          >
                            <DollarSign className="w-4 h-4" />
                            Pay
                          </button>

                          {/* View History Button */}
                          <button
                            onClick={() => openHistoryModal(cust)}
                            title="View History & Ledger"
                            className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors flex items-center gap-1 text-xs font-medium"
                          >
                            <Eye className="w-4 h-4 text-blue-600" />
                            History
                          </button>

                          {/* Download Statement PDF */}
                          <button
                            onClick={() => handleDownloadRowStatement(cust, 'pdf')}
                            title="Download PDF Statement"
                            className="p-2 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded-lg transition-colors flex items-center gap-1 text-xs font-medium"
                          >
                            <Download className="w-4 h-4" />
                            PDF
                          </button>

                          {/* Download Statement Excel */}
                          <button
                            onClick={() => handleDownloadRowStatement(cust, 'excel')}
                            title="Download Excel Ledger"
                            className="p-2 bg-green-50 hover:bg-green-100 text-green-700 border border-green-200 rounded-lg transition-colors flex items-center gap-1 text-xs font-medium"
                          >
                            <FileSpreadsheet className="w-4 h-4" />
                            Excel
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pay Credit Due Modal ($) */}
      {payModalOpen && selectedCustomerForPay && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl border border-gray-200 relative animate-in fade-in zoom-in-95 duration-200">
            <button
              onClick={() => setPayModalOpen(false)}
              className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 p-1"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center text-green-600">
                <DollarSign className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Record Credit Payment</h3>
                <p className="text-xs text-gray-500">{selectedCustomerForPay.name} ({selectedCustomerForPay.mobileNumber})</p>
              </div>
            </div>

            <div className="bg-red-50 p-4 rounded-xl border border-red-100 mb-5 text-center">
              <span className="text-xs font-semibold text-red-600 uppercase tracking-wide">Current Outstanding Balance Due</span>
              <div className="text-2xl font-bold text-red-700 mt-1 font-mono">
                Rs. {(selectedCustomerForPay.creditBalance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
            </div>

            <form onSubmit={handleSubmitPayment} className="space-y-4">
              {/* Option to Pay Full or Custom Partial */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-xs font-bold text-gray-700 uppercase">Amount to Pay (Rs.)</label>
                  <button
                    type="button"
                    onClick={handleToggleFullPayment}
                    className="text-xs text-orange-600 hover:text-orange-700 font-semibold underline"
                  >
                    {isFullPayment ? 'Pay Custom Partial Amount' : 'Pay Full Amount'}
                  </button>
                </div>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={selectedCustomerForPay.creditBalance}
                  value={payAmount}
                  onChange={(e) => handlePayAmountChange(e.target.value)}
                  placeholder="0.00"
                  required
                  className="w-full p-3 border border-gray-300 rounded-xl text-lg font-mono text-right font-bold text-gray-900 focus:ring-2 focus:ring-orange-500 outline-none"
                />
              </div>

              {/* Payment Method */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-2">Payment Method</label>
                <div className="grid grid-cols-3 gap-2">
                  {['cash', 'card', 'bank_transfer'].map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setPaymentMethod(m)}
                      className={`py-2 px-3 rounded-lg text-xs font-semibold capitalize border transition-all ${
                        paymentMethod === m
                          ? 'bg-orange-500 text-white border-orange-500 shadow-sm'
                          : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      {m.replace('_', ' ')}
                    </button>
                  ))}
                </div>
              </div>

              {/* Reference Number */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Ref / Receipt / Cheque No. (Optional)</label>
                <input
                  type="text"
                  placeholder="e.g. REC-10293"
                  value={referenceNumber}
                  onChange={(e) => setReferenceNumber(e.target.value)}
                  className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Notes / Remarks (Optional)</label>
                <textarea
                  rows="2"
                  placeholder="Additional payment notes..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                />
              </div>

              {/* Submit Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setPayModalOpen(false)}
                  className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl text-sm transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingPay}
                  className="flex-1 py-3 bg-green-500 hover:bg-green-600 text-white font-bold rounded-xl text-sm transition-colors flex items-center justify-center gap-2 shadow-sm disabled:opacity-50"
                >
                  {submittingPay ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <DollarSign className="w-4 h-4" />
                  )}
                  <span>Submit Payment</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Credit History & Ledger Modal */}
      {historyModalOpen && historyCustomer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full p-6 shadow-xl border border-gray-200 relative max-h-[90vh] flex flex-col">
            <button
              onClick={() => setHistoryModalOpen(false)}
              className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 p-1"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 border-b border-gray-200 pb-4 pr-8">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600">
                  <FileText className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Credit Ledger & Transaction History</h3>
                  <p className="text-xs text-gray-500">
                    {historyCustomer.name} ({historyCustomer.mobileNumber}) — Current Due: <span className="font-bold text-red-600">Rs. {(historyCustomer.creditBalance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </p>
                </div>
              </div>

              {/* Download Buttons inside History Modal */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => downloadCreditHistoryPDF(historyCustomer, historyTransactions)}
                  disabled={historyTransactions.length === 0}
                  title="Download PDF Statement"
                  className="px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Download className="w-3.5 h-3.5" />
                  PDF Statement
                </button>
                <button
                  onClick={() => downloadCreditHistoryExcel(historyCustomer, historyTransactions)}
                  disabled={historyTransactions.length === 0}
                  title="Download Excel Ledger"
                  className="px-3 py-1.5 bg-green-50 hover:bg-green-100 text-green-700 border border-green-200 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FileSpreadsheet className="w-3.5 h-3.5" />
                  Excel
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto pr-1">
              {loadingHistory ? (
                <div className="py-12 text-center text-gray-500 flex flex-col items-center gap-2">
                  <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
                  <span>Loading history...</span>
                </div>
              ) : historyTransactions.length === 0 ? (
                <div className="py-12 text-center text-gray-400">
                  No credit transactions recorded yet for this customer.
                </div>
              ) : (
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-gray-600 font-semibold uppercase">
                      <th className="py-2.5 px-3">Date & Time</th>
                      <th className="py-2.5 px-3">Type</th>
                      <th className="py-2.5 px-3">Order / Ref</th>
                      <th className="py-2.5 px-3 text-right">Amount</th>
                      <th className="py-2.5 px-3 text-right">Previous Balance</th>
                      <th className="py-2.5 px-3 text-right">New Balance</th>
                      <th className="py-2.5 px-3">Method / Recorded By</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {historyTransactions.map((tx) => {
                      const isCharge = tx.type === 'CHARGE'
                      return (
                        <tr key={tx._id} className="hover:bg-gray-50">
                          <td className="py-3 px-3 text-gray-600 whitespace-nowrap">
                            {new Date(tx.createdAt).toLocaleString()}
                          </td>
                          <td className="py-3 px-3">
                            {isCharge ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700">
                                <ArrowUpRight className="w-3 h-3" /> CHARGE (POS)
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-green-100 text-green-700">
                                <ArrowDownLeft className="w-3 h-3" /> SETTLEMENT
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-3 font-mono text-gray-800">
                            {tx.order ? (tx.order.orderNumber || tx.order._id) : (tx.referenceNumber || '-')}
                          </td>
                          <td className={`py-3 px-3 text-right font-mono font-bold ${isCharge ? 'text-red-600' : 'text-green-600'}`}>
                            {isCharge ? '+' : '-'} Rs. {tx.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </td>
                          <td className="py-3 px-3 text-right font-mono text-gray-500">
                            Rs. {tx.previousBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </td>
                          <td className="py-3 px-3 text-right font-mono font-bold text-gray-900">
                            Rs. {tx.newBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </td>
                          <td className="py-3 px-3 text-gray-600">
                            <div className="font-semibold capitalize">{tx.paymentMethod}</div>
                            <div className="text-[10px] text-gray-400">{tx.recordedBy?.username || 'System'}</div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}
            </div>

            <div className="pt-4 border-t border-gray-200 mt-4 flex justify-end">
              <button
                onClick={() => setHistoryModalOpen(false)}
                className="px-5 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl text-sm transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Credit Customer Modal */}
      {addModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl border border-gray-200 relative animate-in fade-in zoom-in-95 duration-200">
            <button
              onClick={() => setAddModalOpen(false)}
              className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 p-1"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center text-orange-600">
                <UserPlus className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Add Credit Customer</h3>
                <p className="text-xs text-gray-500">Select an existing customer to tag as a Credit Customer.</p>
              </div>
            </div>

            <form onSubmit={handleAddCreditCustomer} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Select Customer</label>
                <SearchableSelect
                  options={allExistingCustomers.map((c) => ({
                    value: c._id,
                    label: `${c.name} (${c.mobileNumber || 'No Phone'})`,
                    subtext: c.email || c.address || ''
                  }))}
                  value={selectedAddCustomerId}
                  onChange={(val) => setSelectedAddCustomerId(val)}
                  placeholder="-- Choose Customer --"
                  searchPlaceholder="Search customer by name or phone..."
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setAddModalOpen(false)}
                  className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl text-sm transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingAdd}
                  className="flex-1 py-3 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-xl text-sm transition-colors flex items-center justify-center gap-2 shadow-sm disabled:opacity-50"
                >
                  {submittingAdd ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <UserPlus className="w-4 h-4" />
                  )}
                  <span>Tag as Credit Customer</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
